import { useState } from 'react';
import { API_URL } from '../../lib/api-client';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function DebugTokenPanel() {
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const decodeToken = () => {
    const token = localStorage.getItem('token');

    if (!token) {
      toast.error('No token found in localStorage');
      return;
    }

    try {
      // Decode JWT manually (split by .)
      const parts = token.split('.');
      if (parts.length !== 3) {
        toast.error('Invalid token format');
        return;
      }

      // Decode the payload (middle part)
      const payload = JSON.parse(atob(parts[1]));

      const now = Date.now() / 1000;
      const isExpired = payload.exp < now;
      const hoursLeft = ((payload.exp - now) / 3600).toFixed(2);

      setTokenInfo({
        userId: payload.userId,
        issuedAt: new Date(payload.iat * 1000).toLocaleString(),
        expiresAt: new Date(payload.exp * 1000).toLocaleString(),
        isExpired,
        hoursLeft: isExpired ? 0 : hoursLeft,
        rawToken: token.substring(0, 50) + '...'
      });

      if (isExpired) {
        toast.error('Token has expired! Please logout and login again.');
      } else {
        toast.success(`Token is valid for ${hoursLeft} more hours`);
      }
    } catch (error) {
      toast.error('Failed to decode token');
      console.error(error);
    }
  };

  const copyToken = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigator.clipboard.writeText(token);
      toast.success('Token copied to clipboard!');
    }
  };

  const testAPI = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // First test the debug endpoint
      console.log('Testing debug-auth endpoint...');
      const debugResponse = await fetch(`${API_URL}/promotional-orders/debug-auth`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const debugData = await debugResponse.json();
      console.log('Debug Auth Response:', debugResponse.status, debugData);

      if (!debugResponse.ok) {
        toast.error(`Debug auth failed: ${debugResponse.status} - ${debugData.message || 'Unknown error'}`);
        return;
      }

      toast.success(`Debug auth OK! Role: ${debugData.user.role}`);

      // Now test the actual settings endpoint
      console.log('Testing settings endpoint...');
      const response = await fetch(`${API_URL}/promotional-orders/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Settings Response:', response.status, data);

      if (response.ok) {
        toast.success('Settings API call successful!');
      } else {
        toast.error(`Settings API failed: ${response.status} - ${data.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Network error: ${error.message}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">🔍 Token Debug Panel</h3>
        <Button onClick={decodeToken} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Decode Token
        </Button>
      </div>

      {tokenInfo && (
        <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-2">
            <div className="font-semibold">User ID:</div>
            <div className="font-mono text-xs">{tokenInfo.userId}</div>

            <div className="font-semibold">Issued At:</div>
            <div>{tokenInfo.issuedAt}</div>

            <div className="font-semibold">Expires At:</div>
            <div>{tokenInfo.expiresAt}</div>

            <div className="font-semibold">Status:</div>
            <div>
              {tokenInfo.isExpired ? (
                <span className="text-red-600 font-semibold">❌ EXPIRED</span>
              ) : (
                <span className="text-green-600 font-semibold">
                  ✅ Valid ({tokenInfo.hoursLeft}h left)
                </span>
              )}
            </div>

            <div className="font-semibold">Token Preview:</div>
            <div className="font-mono text-xs truncate">{tokenInfo.rawToken}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={copyToken} variant="outline" size="sm">
          <Copy className="w-4 h-4 mr-2" />
          Copy Full Token
        </Button>
        <Button onClick={testAPI} disabled={loading} size="sm">
          {loading ? 'Testing...' : 'Test Promo API'}
        </Button>
      </div>

      <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded border border-blue-200">
        <p className="font-semibold mb-1">To debug in backend:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Click "Copy Full Token"</li>
          <li>Open terminal in backend folder</li>
          <li>Run: <code className="bg-white px-1">npm run decode:token YOUR_TOKEN</code></li>
          <li>It will show user details and role from database</li>
        </ol>
      </div>
    </Card>
  );
}